import React, { useEffect, useMemo, useRef } from "react";
import { useAppContext } from "../../contexts/AppContext";
import { useSelection } from "../../hooks/useSelection";
import { useFileListProjection } from "../../hooks/useFileListProjection";
import { useIsMobile } from "../../hooks/useMobile";
import useEditorMetadataModel from "./useEditorMetadataModel";
import useMetadataIssues from "./useMetadataIssues";
import StandardQueuePane from "../FileQueue/StandardQueuePane";
import MetadataStudioPane from "./MetadataStudioPane";
import AssistantPane from "./AssistantPane";
import ThreePaneWorkspace from "../Workspace/ThreePaneWorkspace";
import { WORKSPACE_VIEW_META } from "../Workspace/workspaceViewMeta";
import { ViewIndicator } from "../ViewIndicator";

function Editor() {
  const editorViewMeta = WORKSPACE_VIEW_META.editor;
  const {
    files,
    fileListSort,
    fileListFilters,
    editorRightPanelOpen,
    setEditorRightPanelOpen,
    editorMobileLeftOpen,
    setEditorMobileLeftOpen,
    editorMobileRightOpen,
    setEditorMobileRightOpen,
  } = useAppContext();
  const { selectedIndices, selectSingle, clearSelection } = useSelection();
  const { visibleIndices } = useFileListProjection({
    files,
    sort: fileListSort,
    filters: fileListFilters,
  });
  const visibleIndexSet = useMemo(
    () => new Set(visibleIndices),
    [visibleIndices],
  );
  const containerRef = useRef(null);
  const isMobile = useIsMobile();

  const model = useEditorMetadataModel();
  const issuesApi = useMetadataIssues({ model });

  useEffect(() => {
    if (visibleIndices.length === 0) {
      if (selectedIndices.size > 0) {
        clearSelection();
      }
      return;
    }

    const hasVisibleSelection = Array.from(selectedIndices).some((index) =>
      visibleIndexSet.has(index),
    );
    if (!hasVisibleSelection) {
      selectSingle(visibleIndices[0]);
    }
  }, [
    visibleIndices,
    visibleIndexSet,
    selectedIndices,
    selectSingle,
    clearSelection,
  ]);

  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  return (
    <main
      ref={containerRef}
      tabIndex={-1}
      className="relative flex h-full flex-1 flex-col bg-linear-to-br from-bg-primary to-bg-deep outline-none"
    >
      <ThreePaneWorkspace
        isMobile={isMobile}
        leftPane={
          <StandardQueuePane
            horizontal={isMobile}
            headerContent={
              <ViewIndicator
                label={editorViewMeta.label}
                icon={editorViewMeta.icon}
              />
            }
          />
        }
        centerPane={
          <MetadataStudioPane
            model={model}
            issuesApi={issuesApi}
            desktopRightOpen={editorRightPanelOpen}
            onToggleDesktopRight={() =>
              setEditorRightPanelOpen((prev) => !prev)
            }
          />
        }
        rightPane={
          <AssistantPane
            onHideDesktopRight={() => setEditorRightPanelOpen(false)}
          />
        }
        desktopRightOpen={editorRightPanelOpen}
        mobileLeftOpen={editorMobileLeftOpen}
        onMobileLeftOpenChange={setEditorMobileLeftOpen}
        mobileRightOpen={editorMobileRightOpen}
        onMobileRightOpenChange={setEditorMobileRightOpen}
        mobileLeftSheetSide="bottom"
        mobileRightSheetSide="right"
      />
    </main>
  );
}

export default Editor;
